import {
  Box,
  Button,
  Card,
  CheckboxInput,
  Icon,
  MessageBlock,
  NavList,
  StickyFooter,
  Switch,
  UserIcon,
} from '@deckee/deck-hand';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { captureEvent } from '../../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../../services/feature';
import { useAppDispatch } from '../../../../../services/store';
import { selectCurrentMember } from '../../../../../services/store/modules/backend-service';
import {
  addOrUpdateEmergencyContact,
  fetchExistingEmergencyContacts,
  removeSelectedEmergencyContact,
  selectAllEmergencyContacts,
  selectPlanIsLoggingOn,
  selectSelectedEmergencyContacts,
} from '../../../../../services/store/modules/plan';
import { PlanEmergencyContact } from '../../../../../services/store/modules/plan/models';
import BasicDrawerLayout from '../../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import IconWithTextLockup from '../../../../lockups/IconWithTextLockup/IconWithTextLockup';
import EmptyInfoPanel from '../../../../shared/EmptyInfoPanel/EmptyInfoPanel';
import { ReactSvg, pencil } from '../../../../shared/vector-images';

const CircleWrapper: FC<{
  bg?: string;
  iconSize?: number;
  boxSize?: number;
  className?: string;
  children?: React.ReactNode;
}> = ({ children, bg, iconSize = 24, boxSize = 40, className }) => {
  const theme = useTheme();
  return (
    <Box
      className={className}
      width={boxSize}
      height={boxSize}
      border="1px solid"
      position="absolute"
      right="-5px"
      top="-5px"
      borderColor={theme.colors.success}
      display="flex"
      flex="0 0 auto"
      justifyContent="center"
      alignItems="center"
      borderRadius="50%"
      bg={bg}
    >
      <Box
        width={iconSize}
        height={iconSize}
        flex={`0 0 ${iconSize}px`}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {children}
      </Box>
    </Box>
  );
};
CircleWrapper.defaultProps = {
  bg: '#F5F5F5',
  iconSize: 24,
  boxSize: 40,
  className: undefined,
};

interface PencilEditIconProps {
  handleClickEdit: (id: string) => void;
}
const PencilEditIcon: FC<PencilEditIconProps> = ({ handleClickEdit }) => {
  const theme = useTheme();
  return (
    <Box position="relative" onClick={handleClickEdit}>
      <CircleWrapper iconSize={18} boxSize={20} bg="white">
        <ReactSvg
          toDisplay={pencil()}
          options={{
            fillColor: theme.colors.success,
          }}
          width={18}
          height={18}
        />
      </CircleWrapper>

      <Icon.Circle bg={theme.colors.primary}>
        <UserIcon color="white" />
      </Icon.Circle>
    </Box>
  );
};

export const StyledNavList = styled(NavList)`
  padding: ${({ theme }) => theme.space[2]}px;
`;

const EmergencyContactsListPage: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const contacts = useSelector(selectAllEmergencyContacts);

  const selectedEmergencyContacts = useSelector(
    selectSelectedEmergencyContacts,
  );

  const [error, setError] = useState('');
  const isLoloUser = hasFeatureEnabled('log-on-log-off');
  const isLoggingOn = useSelector(selectPlanIsLoggingOn);
  const [logonWithWa] = useState(
    isLoggingOn === undefined ? isLoloUser : isLoggingOn,
  );

  const currentMember = useSelector(selectCurrentMember);

  const handleToggleContact = (contact: PlanEmergencyContact) => {
    captureEvent({
      type: 'plan: click toggle emergency contact',
      payload: { id: contact.id, selected: !contact.selected },
    });

    if (contact.selected) {
      dispatch(removeSelectedEmergencyContact(contact.id));
    } else {
      dispatch(addOrUpdateEmergencyContact(contact));
    }
  };

  const handleClickEdit = (id: string) => {
    captureEvent({
      type: 'plan: click edit emergency contact',
      payload: {},
    });

    history.push(`/maps/plan/emergency-contacts/edit/${id}`);
  };

  const handleAddContact = () => {
    captureEvent({
      type: 'plan: click add new emergency contact',
      payload: {},
    });
    history.push('/maps/plan/emergency-contacts/add');
  };

  const handleContinue = () => {
    captureEvent({
      type: 'plan: click continue from emergency contacts',
      payload: {},
    });

    if (
      logonWithWa &&
      selectedEmergencyContacts.filter(
        (contact) => contact.contactType === 'onland',
      ).length === 0
    ) {
      setError(
        'You must select at least one onland emergency contact when logging on.',
      );
      return;
    }

    if (
      logonWithWa &&
      selectedEmergencyContacts.filter(
        (contact) =>
          contact.contactType === 'onboard' && !contact.contactNumber,
      ).length > 0
    ) {
      setError(
        'You must enter a phone number for all emergency contacts onboard when logging on.',
      );
      return;
    }

    history.push('/maps/plan/create');
  };

  const handleClickAddPhoneNumber = () => {
    captureEvent({
      type: 'plan: click add phone number',
      payload: {},
    });

    history.push(`/maps/plan/emergency-contacts/update-member`);
  };

  const handleToggleOnboardOnland = (
    contact: PlanEmergencyContact,
    onland: boolean,
  ) => {
    captureEvent({
      type: 'plan: click toggle onboard onland',
      payload: { contact, onland },
    });

    dispatch(
      addOrUpdateEmergencyContact({
        ...contact,
        contactType: onland ? 'onland' : 'onboard',
      }),
    );
  };

  useEffect(() => {
    dispatch(fetchExistingEmergencyContacts());
  }, [dispatch]);

  const handleClickYouCheckbox = () => {
    captureEvent({
      type: 'plan: click toggle you checkbox',
      payload: {},
    });
  };

  return (
    <BasicDrawerLayout
      heading="Emergency contacts"
      showMenuBar={false}
      onBack={() => history.goBack()}
      onClose={null}
    >
      <Box height="100%" display="flex" flexDirection="column">
        {error && <MessageBlock type="error">{error}</MessageBlock>}

        <Box flex="1 0 0px">
          {contacts.length === 0 && (
            <EmptyInfoPanel>
              Your emergency contacts will show here once you have added them to
              your plan
            </EmptyInfoPanel>
          )}

          {contacts.length !== 0 && (
            <StyledNavList grouped>
              <Card>
                <NavList.Item py="2">
                  <Box
                    display="flex"
                    flexDirection="column"
                    flex="1 1 100%"
                    pt="2"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <IconWithTextLockup
                        useEllipsis
                        flex="1 1 100%"
                        heading={
                          currentMember?.fullName
                            ? `${currentMember.fullName} (you)`
                            : 'You'
                        }
                        emphasis="heading"
                        descriptionColor="black"
                        description={
                          currentMember?.phoneNumber &&
                          currentMember?.fullName ? (
                            currentMember?.phoneNumber
                          ) : (
                            <Box
                              color="danger"
                              fontWeight="bold"
                              onClick={handleClickAddPhoneNumber}
                            >
                              {isLoggingOn
                                ? // If logging on, it's mandatory
                                  'More details needed'
                                : 'Add contact information'}
                            </Box>
                          )
                        }
                        image={
                          <PencilEditIcon
                            handleClickEdit={handleClickAddPhoneNumber}
                          />
                        }
                      />
                      <Box mr="2">
                        <CheckboxInput
                          checked
                          onClick={handleClickYouCheckbox}
                        />
                      </Box>
                    </Box>
                  </Box>
                </NavList.Item>
              </Card>

              {contacts.map((contact) => (
                <Card key={contact.id}>
                  <NavList.Item py="2" useEllipsis>
                    <Box
                      display="flex"
                      flexDirection="column"
                      flex="1 1 100%"
                      pt="2"
                      width="100%"
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <IconWithTextLockup
                          useEllipsis
                          flex="1 1 100%"
                          image={
                            <PencilEditIcon
                              handleClickEdit={() =>
                                handleClickEdit(contact.id)
                              }
                            />
                          }
                          heading={contact.name}
                          emphasis="heading"
                          descriptionColor="black"
                          description={
                            <>
                              {contact.email}
                              <br />
                              {contact.contactNumber}
                            </>
                          }
                        />

                        <Box mr="2">
                          <CheckboxInput
                            onChange={() => handleToggleContact(contact)}
                            checked={contact.selected}
                          />
                        </Box>
                      </Box>
                      {contact.selected && (
                        <Box mt="2">
                          <Switch.Group
                            name={contact.id}
                            value={contact.contactType}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              handleToggleOnboardOnland(
                                contact,
                                e.target.value === 'onland',
                              );
                            }}
                          >
                            <Switch id={`onland${contact.id}`} value="onland">
                              On land
                            </Switch>
                            <Switch id={`onboard${contact.id}`} value="onboard">
                              Onboard
                            </Switch>
                          </Switch.Group>
                        </Box>
                      )}
                    </Box>
                  </NavList.Item>
                </Card>
              ))}
            </StyledNavList>
          )}
        </Box>
        <StickyFooter>
          <Button onClick={handleAddContact} variant="ghost" wide>
            Add new contact
          </Button>

          <Button wide onClick={handleContinue}>
            Continue
          </Button>
        </StickyFooter>
      </Box>
    </BasicDrawerLayout>
  );
};

export default EmergencyContactsListPage;
